import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  useMediaQuery,
  MenuItem,
  Fab,
} from "@mui/material";

import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import { useInView } from "react-intersection-observer";
import { useDebounce } from "use-debounce";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import { useTheme } from "@emotion/react";
import useQuotesAdmin from "../../../hooks/useQuotesAdmin";
import { AdminQuoteAndOrderCountContext } from "../../../context/AdminQuoteAndOrderCountContext";
import CreateNewQuoteDialog from "./CreateNewQuoteDialog";
import QuoteRow from "./QuoteRow";
import TextFieldDebounced from "../../../components/TextFieldDebounced";
import { Add } from "@mui/icons-material";

const QuoteRequests = ({ search, status }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [newQuoteModalOpen, setNewQuoteModalOpen] = useState(false);

  const { authToken, ezorder } = useContext(AuthContext);
  const [
    orders,
    onRefresh,
    refreshing,
    onLoadMore,
    loadingMore,
    total,
    hasMore,
  ] = useQuotesAdmin(authToken, 50, null, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate || statusUpdate) {
      if (searchUpdate) {
        query.push(`search=${searchUpdate}`);
      }
      if (statusUpdate) {
        query.push(`status=${statusUpdate}`);
      }
    }
    if (query.length > 0) {
      navigate(`/quotes?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/quotes`);
    }
  };

  useEffect(() => {
    if (orders.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <CreateNewQuoteDialog
        open={newQuoteModalOpen}
        onClose={() => {
          setNewQuoteModalOpen(false);
        }}
      />

      <Typography variant="h4" component="div">
        Quotes
      </Typography>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          sx={{ width: "200px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"DRAFT_AND_SENT"}>Draft and Sent</MenuItem>
          <MenuItem value={"DRAFT"}>Draft</MenuItem>
          <MenuItem value={"SENT"}>Sent</MenuItem>
          <MenuItem value={"SOLD"}>Sold</MenuItem>
          <MenuItem value={"ALL"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        TableContainerComponent={Paper}
        TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: "155px" }}>
                Quote Status
              </TableCell>
              <TableCell align="left">Sales Rep</TableCell>
              <TableCell align="left">ID</TableCell>
              <TableCell align="left">Customer Name</TableCell>
              <TableCell align="left">Project</TableCell>
              <TableCell align="left">PO #</TableCell>
              <TableCell align="left">Date Created</TableCell>
              <TableCell align="left">Quote Sent</TableCell>
              <TableCell align="left">$</TableCell>
            </TableRow>
          </TableHead>
        )}
        items={orders}
        renderRow={(item, index) => {
          return (
            <QuoteRow
              key={index}
              isPendingQuote={true}
              order={item}
              onSelect={(order) => {
                navigate(`/quotes/${order.id}`);
              }}
            />
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Fab
          onClick={() => {
            navigate("/quotes/create");
          }}
          variant="extended"
          color="success"
        >
          <Add /> Quote
        </Fab>
      </div>
    </Box>
  );
};
export default QuoteRequests;
